/* eslint-disable @typescript-eslint/naming-convention */
import apiServices from 'config/apiServiceApiGateway'

export type InboundTaskFilterType = {
  invoice_number: string
  purchase_order_number: string
  search_product: string
  location_ids: number[]
}

export type InboundTaskQueryParamsType = InboundTaskFilterType & {
  page_index: number
  page_size: number
}

type ProductImageType = {
  url_large: string
  url_medium: string
  url_small: string
}

type ProductDataType = {
  id: number
  product_age: number
  sku_number: string
  name: string
  uom: string
  image: ProductImageType
}

type DestinationLocationType = {
  id: number
  name: string
  type: string
}

export type InboundTask = {
  id: number
  invoice_number: string
  purchase_order_numbers: string[]
  product: ProductDataType
  sku_type: string
  total_qty: number
  carton_qty: number
  vendor_id: number
  destination_location: DestinationLocationType
  inbound_date: string
  is_partial: boolean
  actual_qty: number
  status: 'CREATED' | 'IN_PROGRESS' | 'SUBMITTED' | 'COMPLETED'
}

export type GetInboundTaskListResponseType = {
  data: InboundTask[]
  pagination: {
    page_index: number
    page_size: number
  }
}

type InboundTaskDetailActivityType = {
  action: string
  status: string
  start_at: string
  end_at?: string
  staff: {
    id: number
    name: string
  }
}

type InboundTaskReceivedItemsType = {
  id: number
  asset: {
    id: number
    name: string
    type: string
  }
  expiry_date: string
  qty: number
}

export type GetInboundTaskDetailResponseType = {
  data: {
    parent_inbound_task_id: number
    purchase_order_inbound_task_id: number
    purchase_order_numbers: string[]
    invoice_number: string
    product: ProductDataType
    sku_type: string
    total_qty: number
    carton_qty: number
    activities: InboundTaskDetailActivityType[]
    received_items: InboundTaskReceivedItemsType[]
    vendor: {
      id: number
      name: string
    }
    received_qty: number
    category: string
    destination_location: DestinationLocationType
    status: string
    inbound_date: string
  }
}

type InboundTaskCheckQtyItemSummaryProductType = {
  id: number
  name: string
  sku_number: string
  uom: string
  product_age: number
  is_qr: boolean
  is_new_sku: boolean
  business_tagging_labels: string[]
}

export type InboundTaskCheckQtyItemSummaryType = {
  id: string
  product: InboundTaskCheckQtyItemSummaryProductType
  request_quantity: number
  received_quantity: number
  package_ids: string[]
  status_notes: {
    id: string
    name: string
  }[]
}

export type GetInboundTaskCheckQtyItemsSummaryResponseType = {
  data: InboundTaskCheckQtyItemSummaryType[]
}

interface DefaultNoDataResponseInterface {
  message: string
  error: {
    status: boolean
    message: string
    code: number
  }
}

type PostInboundTasksPartialGrnRequestType = {
  purchase_order_inbound_task_id: number
  product_id: number
  partials: {
    reason_id: number
    qty: number
  }[]
}

export type GetReasonsRequestParamsType = {
  location_id: number
  type: 'CANCEL' | 'PARTIAL_COMPLETE' | 'MANUAL_SCAN_PO' | 'MANUAL_SCAN_LOADING_GATE'
}

export const getInboundTaskList = (params: InboundTaskQueryParamsType) =>
  apiServices.Get<GetInboundTaskListResponseType, true>({
    url: '/wims/internal/v1/purchase-order-inbound-tasks',
    params,
    config: { paramsSerializer: { indexes: null } },
  })

export const getInboundTaskDetail = (id: string) =>
  apiServices.Get<GetInboundTaskDetailResponseType, true>({
    url: `/wims/internal/v1/purchase-order-inbound-tasks/${id}`,
  })

export const getInboundTaskCheckQtyItems = (id: string) =>
  apiServices.Get({
    url: `/wims/internal/v1/purchase-order-inbounds/${id}/items`,
  })

export const getInboundTaskCheckQtyItemsSummary = (id: number) =>
  apiServices.Get<GetInboundTaskCheckQtyItemsSummaryResponseType, true>({
    url: `/wims/internal/v1/purchase-order-inbounds/${id}/items-summary`,
  })

export const postCompleteFlexibleInboundCheckQty = (id: number) =>
  apiServices.Post<DefaultNoDataResponseInterface, true>({
    url: `/wims/internal/v1/purchase-order-inbounds/${id}:complete`,
  })

export const postInboundTasksPartialGrn = (data: PostInboundTasksPartialGrnRequestType) =>
  apiServices.Post<DefaultNoDataResponseInterface, true>({
    url: '/wims/internal/v1/purchase-order-inbound-tasks:updatePartial',
    data,
  })

export type ReasonType = {
  id: number
  reason: string
}

export const getPoReasons = (params: GetReasonsRequestParamsType) =>
  apiServices.Get<ReasonType[]>({
    url: '/wms/internal/v1/purchase-order-reasons',
    params,
  })
